import * as React from 'react';
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import { Link, Stack, Typography, Grid, Skeleton, Paper } from '@mui/material';
import { colors } from 'styles/colors';
import { completeDateFormat } from 'utilities/formatDate';
import { ICandidateProfileApplicationHistory } from 'modules/hiring/models/candidateProfile.model';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

export interface ICandidateActivityHistoryProps {
  data: ICandidateProfileApplicationHistory | undefined;
  loading: boolean;
}

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const CandidateActivitySchimmer = () => {
  return (
    <Grid
      id='candidate-applications-activity-schimmer'
      data-testid='candidate-applications-activity-schimmer'
      container
      direction={'column'}
    >
      {[1, 2, 3].map((_, i: number) => (
        <Grid item key={`shimmer-${i}`}>
          <Stack direction='row'>
            <Item sx={{ border: 'none' }}>
              <Skeleton
                variant='circular'
                width={30}
                height={30}
                sx={{ bgcolor: (theme) => theme.palette.neutral?.main }}
              />
            </Item>
            <Item sx={{ width: '100%', border: 'none' }}>
              <Skeleton
                animation='wave'
                sx={{
                  width: '100%',
                  height: '1.688rem',
                  bgcolor: (theme) => theme.palette.neutral?.main,
                }}
              />
              <Skeleton
                animation='wave'
                sx={{
                  width: '30%',
                  height: '1.25rem',
                  mt: '0.625rem',
                  bgcolor: (theme) => theme.palette.neutral?.main,
                }}
              />
            </Item>
          </Stack>
        </Grid>
      ))}
    </Grid>
  );
};

const CandidateActivityHistory = (props: ICandidateActivityHistoryProps) => {
  const { data, loading } = props;
  const activityHistory = data?.activityHistory;
  const uniqueActivityHistory = activityHistory?.filter(
    (item, index, self) =>
      index ===
      self.findIndex(
        (t) =>
          t.activityName === item.activityName &&
          t.activityStatus === item.activityStatus &&
          t.activityDate === item.activityDate &&
          t.jobRequistionId === item.jobRequistionId
      )
  );
  const candidateFullName = `${data?.firstName} ${data?.lastName}`;
  const { t } = useTranslation();

  return (
    <>
      {loading ? (
        <CandidateActivitySchimmer />
      ) : (
        <Timeline
          data-testid='candidate-activity-history'
          sx={{
            mt: '1.25rem',
            mb: '0.375rem',
            p: '1.25rem',
            pb: '0.75rem',
            borderTop: '1px solid #F2F2F2',
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
            },
            '& li': {
              minHeight: 0,
              ':not(:last-child)': {
                height: 'auto',
              },
            },
          }}
        >
          {!!uniqueActivityHistory?.length &&
            uniqueActivityHistory
              ?.sort(
                (a, b) =>
                  new Date(b?.activityDate)?.getTime() -
                  new Date(a?.activityDate)?.getTime()
              )
              .map((activity, index) => {
                const formatedDate = completeDateFormat(activity.activityDate);
                return (
                  <TimelineItem key={index}>
                    <TimelineSeparator
                      sx={{
                        transform: 'translateY(0.25rem)',
                      }}
                    >
                      <TimelineDot
                        variant='outlined'
                        sx={{
                          borderColor: colors.primaryDark,
                          marginY: 0,
                          width: '1rem',
                          height: '1rem',
                        }}
                      />
                      {uniqueActivityHistory &&
                        index < uniqueActivityHistory.length - 1 && (
                          <TimelineConnector
                            sx={{
                              borderLeft: `0.125rem dashed ${colors.connector.border}`,
                              backgroundColor: 'white',
                              width: 0,
                            }}
                          />
                        )}
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{
                        p: 0,
                        pl: '1rem',
                        pb: '1rem',
                      }}
                    >
                      <div>
                        <Typography
                          className='kfic2-bodyText'
                          sx={{ wordBreak: 'break-word' }}
                        >
                          <Link
                            data-testid={`candidate-name-${index}`}
                            sx={{
                              fontWeight: 700,
                              textDecoration: 'none',
                              color: colors.primaryDark,
                            }}
                          >
                            {candidateFullName}
                          </Link>{' '}
                          {`${t('isAt')} `}
                          <span data-testid={`candidate-activityName-${index}`}>
                            {activity?.activityName}
                          </span>{' '}
                          {`${t('withStatus')} `}
                          <span
                            data-testid={`candidate-activityStatus-${index}`}
                          >
                            {activity?.activityStatus}
                          </span>
                          {` ${t('forReq')} `}
                          <span
                            data-testid={`candidate-jobRequistionId-${index}`}
                          >
                            {activity?.jobRequistionId}
                          </span>
                        </Typography>
                        <Typography
                          sx={{ m: 0, color: '#757575 !important' }}
                          className='kfic2-bodyTextSmall'
                        >
                          {formatedDate}
                        </Typography>
                      </div>
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
        </Timeline>
      )}
    </>
  );
};

export default CandidateActivityHistory;
